/**
 * @name: Collection Filters (drawer + sync)
 * @author: Cursor (Agent)
 * @date: 2026-06-26
 * @description: Mobile drawer and sidebar/drawer checkbox sync for main-collection faceted filters.
 * @dependencies: main.js FilterContainer, main-category-filters-ui.js
 * @strict-mode: true
 */

(function collectionFiltersModule() {
  const MOBILE_FILTER_MAX_WIDTH = 1024;

  const isMobileFilterViewport = () => window.innerWidth <= MOBILE_FILTER_MAX_WIDTH;

  const getFilterContainer = () =>
    document.querySelector('filter-container.filter-container--mc-design');

  const getFilterCheckboxes = () => [...document.querySelectorAll('.filter-container--mc-design .filter-checkbox')];

  const normalizeLabel = (text) =>
    (text || '')
      .replace(/\(\d+\)/g, '')
      .replace(/\s+/g, ' ')
      .trim();

  const getLabel = (input) =>
    normalizeLabel(input.dataset.label || input.closest('label')?.textContent || 'Filter');

  const syncCheckboxesByLabel = (label, checked) => {
    for (const cb of getFilterCheckboxes()) {
      if (getLabel(cb) === label) {
        cb.checked = checked;
      }
    }
  };

  const getSidebarFilterForm = () => {
    const form = document.querySelector('#CollectionFilterForm');
    return form instanceof HTMLFormElement ? form : null;
  };

  const isPriceRangeActive = (form) => {
    const minInput = form.querySelector('.cc-price-range__input--min');
    const maxInput = form.querySelector('.cc-price-range__input--max');
    const minVal = minInput?.value?.trim() ?? '';
    const maxVal = maxInput?.value?.trim() ?? '';
    const hasMin = minVal !== '' && minVal !== '0';
    const hasMax = maxVal !== '';
    return hasMin || hasMax;
  };

  const getActiveFilterCount = () => {
    const form = getSidebarFilterForm();
    if (!form) return 0;

    let count = 0;

    for (const input of form.querySelectorAll('.filter-checkbox:checked')) {
      if (input.name === 'sort_by') continue;
      if (input.name === 'filter.v.availability' && input.value === '') continue;
      count += 1;
    }

    if (isPriceRangeActive(form)) {
      count += 1;
    }

    return count;
  };

  const getActiveFilterChipItems = () => {
    const form = getSidebarFilterForm();
    if (!form) return [];

    /** @type {{ label: string, remove: () => void }[]} */
    const items = [];
    const seenLabels = new Set();

    for (const input of form.querySelectorAll('.filter-checkbox:checked')) {
      if (input.name === 'sort_by') continue;
      if (input.name === 'filter.v.availability' && input.value === '') continue;

      const label = getLabel(input);
      if (seenLabels.has(label)) continue;
      seenLabels.add(label);

      items.push({
        label,
        remove: () => {
          syncCheckboxesByLabel(label, false);
        },
      });
    }

    if (isPriceRangeActive(form)) {
      const minInput = form.querySelector('.cc-price-range__input--min');
      const maxInput = form.querySelector('.cc-price-range__input--max');
      const minVal = minInput?.value?.trim() ?? '';
      const maxVal = maxInput?.value?.trim() ?? '';
      const minDisplay = minVal && minVal !== '0' ? minVal : '0';
      const maxDisplay = maxVal || maxInput?.placeholder || '';

      items.push({
        label: `${minDisplay} - ${maxDisplay}`,
        remove: () => {
          if (minInput instanceof HTMLInputElement) {
            minInput.value = '';
            minInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
          if (maxInput instanceof HTMLInputElement) {
            maxInput.value = '';
            maxInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        },
      });
    }

    return items;
  };

  const renderFilterChips = () => {
    const activeFiltersBar = document.getElementById('activeFilters');
    if (!activeFiltersBar) return;

    const items = getActiveFilterChipItems();
    activeFiltersBar.innerHTML = '';

    for (const item of items) {
      const chip = document.createElement('div');
      chip.className = 'filter-chip';
      chip.dataset.value = item.label;

      const labelSpan = document.createElement('span');
      labelSpan.textContent = item.label;

      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.setAttribute('aria-label', `Remove ${item.label}`);
      removeBtn.textContent = '×';
      removeBtn.addEventListener('click', () => {
        item.remove();
        renderFilterChips();
        submitCollectionFilters(false);
      });

      chip.append(labelSpan, removeBtn);
      activeFiltersBar.appendChild(chip);
    }

    activeFiltersBar.hidden = items.length === 0;
    updateFilterCounts();
  };

  const updateFilterCounts = () => {
    const count = getActiveFilterCount();
    const text = count ? `| ${count}` : '';
    for (const el of document.querySelectorAll('#topFilterCount, #drawerFilterCount')) {
      el.textContent = text;
    }
  };

  const submitCollectionFilters = (fromDrawer = false) => {
    const root = getFilterContainer();
    if (!root) return;

    const formId = fromDrawer ? 'CollectionFilterFormDrawer' : 'CollectionFilterForm';
    const form = root.querySelector(`#${formId}`) || document.getElementById(formId);
    if (!(form instanceof HTMLFormElement)) return;

    if (root.dataset.ajaxFiltering === 'true' && typeof root.ajaxLoadForm === 'function') {
      root.ajaxLoadForm({ type: 'submit', target: form, preventDefault() {} });
      return;
    }

    form.requestSubmit();
  };

  const initDrawer = () => {
    const filterDrawer = document.getElementById('filterDrawer');
    const filterOverlay = document.getElementById('filter-overlay');
    const openFilterBtn = document.getElementById('openFilterBtn');
    const drawerCloseBtn = document.getElementById('closeFilter');
    const drawerApplyBtn = document.getElementById('applyFilter');

    if (!filterDrawer || !openFilterBtn) return;

    /** @type {Map<HTMLInputElement, boolean> | null} */
    let drawerFilterSnapshot = null;

    const captureDrawerFilterSnapshot = () => {
      const snapshot = new Map();
      for (const cb of getFilterCheckboxes()) {
        snapshot.set(cb, cb.checked);
      }
      drawerFilterSnapshot = snapshot;
    };

    const restoreDrawerFilterSnapshot = () => {
      if (!drawerFilterSnapshot) return;
      drawerFilterSnapshot.forEach((checked, cb) => {
        cb.checked = checked;
      });
      drawerFilterSnapshot = null;
      window._theme?.MainCategoryFiltersUI?.syncAllAvailabilityPills?.();
      renderFilterChips();
    };

    const discardDrawerFilterSnapshot = () => {
      drawerFilterSnapshot = null;
    };

    const openFilterDrawer = () => {
      if (!isMobileFilterViewport()) return;
      captureDrawerFilterSnapshot();
      window._theme?.MainCategoryFiltersUI?.syncAllAvailabilityPills?.();
      filterDrawer.classList.add('active');
      filterOverlay?.classList.add('active');
      document.body.classList.add('menu-open');
    };

    const closeFilterDrawer = ({ restorePending = true } = {}) => {
      if (restorePending) {
        restoreDrawerFilterSnapshot();
      } else {
        discardDrawerFilterSnapshot();
      }
      filterDrawer.classList.remove('active');
      filterOverlay?.classList.remove('active');
      document.body.classList.remove('menu-open');
    };

    openFilterBtn.addEventListener('click', (event) => {
      if (!isMobileFilterViewport()) return;
      event.preventDefault();
      openFilterDrawer();
    });

    drawerCloseBtn?.addEventListener('click', () => closeFilterDrawer({ restorePending: true }));

    drawerApplyBtn?.addEventListener('click', () => {
      submitCollectionFilters(true);
      closeFilterDrawer({ restorePending: false });
    });

    filterOverlay?.addEventListener('click', () => closeFilterDrawer({ restorePending: true }));

    window.addEventListener('resize', () => {
      if (!isMobileFilterViewport()) {
        closeFilterDrawer({ restorePending: true });
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeFilterDrawer({ restorePending: true });
      }
    });
  };

  const initCheckboxSync = () => {
    const root = getFilterContainer();
    if (!root) return;

    if (!document.documentElement.hasAttribute('data-collection-filter-sync-bound')) {
      document.documentElement.setAttribute('data-collection-filter-sync-bound', '');

      document.addEventListener('change', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement)) return;
        if (!target.classList.contains('filter-checkbox')) return;
        if (!target.closest('filter-container.filter-container--mc-design, #filterDrawer')) return;

        if (target.type === 'radio' && target.name === 'filter.v.availability') {
          window._theme?.MainCategoryFiltersUI?.syncAvailabilityRadios?.(target);
          const inDrawer = Boolean(target.closest('#filterDrawer'));

          if (inDrawer && isMobileFilterViewport()) {
            updateFilterCounts();
            return;
          }

          renderFilterChips();
          return;
        }

        const label = getLabel(target);
        const inDrawer = Boolean(target.closest('#filterDrawer'));

        if (inDrawer && isMobileFilterViewport()) {
          syncCheckboxesByLabel(label, target.checked);
          updateFilterCounts();
          return;
        }

        syncCheckboxesByLabel(label, target.checked);
        renderFilterChips();
      });

      document.addEventListener('change', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement)) return;
        if (!target.classList.contains('cc-price-range__input')) return;
        if (!target.closest('filter-container.filter-container--mc-design, #filterDrawer')) return;
        renderFilterChips();
      });
    }

    renderFilterChips();
  };

  const initClearAllAjax = () => {
    document.addEventListener('click', (event) => {
      const link = event.target.closest('[data-collection-clear-all]');
      if (!(link instanceof HTMLAnchorElement)) return;

      const fc = getFilterContainer();
      if (!fc || fc.dataset.ajaxFiltering !== 'true') return;

      event.preventDefault();
      if (typeof fc.ajaxLoadUrl === 'function') {
        fc.ajaxLoadUrl(link.href);
      } else {
        window.location.assign(link.href);
      }
    });
  };

  const init = () => {
    if (!document.querySelector('filter-container.filter-container--mc-design')) return;
    initDrawer();
    initCheckboxSync();
    initClearAllAjax();

    window._theme = window._theme || {};
    window._theme.CollectionFilters = {
      updateCounts: updateFilterCounts,
      renderChips: renderFilterChips,
    };
  };

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();
